import { FeatureTypes } from "entity-app/constants";
import {
  AI_LINK_TAB_STATE,
  TAiLinkFeedbackItem,
  TAiLinkTabState,
  TAiRecommenderFeedback,
  TEditingEntity,
  TEntityLink
} from "../models";

export type TLinkRecommenderFeedbackRequestItem = {
  activity_id: string | null;
  material_id: string;
  tab_state: TAiLinkTabState;
};

const EMPTY_FEEDBACK: TAiRecommenderFeedback = {
  hasOpenedAiTab: false,
  items: []
};

export const getAiRecommenderFeedback = (
  entity: TEditingEntity | null | undefined
): TAiRecommenderFeedback => entity?.aiRecommenderFeedback ?? EMPTY_FEEDBACK;

export const isLinkingActivitiesWithMaterial = (
  editingEntity?: TEditingEntity | null,
  selectedEntityInfo?: TEntityLink
): boolean =>
  Boolean(editingEntity?.parentEntity) ||
  selectedEntityInfo?.feature_type_id !== FeatureTypes.SUBMITTALS;

export const isAiRecommendationTabVisible = ({
  editingEntity,
  selectedEntityInfo,
  smartLinkingEnabled,
  hasCompletedRecommenderJob
}: {
  editingEntity?: TEditingEntity | null;
  selectedEntityInfo?: TEntityLink;
  smartLinkingEnabled: boolean;
  hasCompletedRecommenderJob: boolean;
}): boolean =>
  Boolean(
    isLinkingActivitiesWithMaterial(editingEntity, selectedEntityInfo) &&
    smartLinkingEnabled &&
    hasCompletedRecommenderJob
  );

export const getMaterialIdsForAiFeedback = (
  editingEntity: TEditingEntity,
  selectedEntityInfo?: TEntityLink
): string[] => {
  if (editingEntity.parentEntity) {
    const materialId =
      editingEntity.parentEntity.link.featureInstanceId ||
      editingEntity.parentEntity.link.feature_instance?.id;
    return materialId ? [materialId] : [];
  }

  if (selectedEntityInfo?.feature_type_id === FeatureTypes.SUBMITTALS) {
    return [];
  }

  if (selectedEntityInfo?.ids?.length) {
    return selectedEntityInfo.ids;
  }

  return editingEntity.entityDetail?.id ? [editingEntity.entityDetail.id] : [];
};

export const recordAiActivityLinked = (
  feedback: TAiRecommenderFeedback,
  {
    activityId,
    materialIds
  }: {
    activityId: string;
    materialIds: string[];
  }
): TAiRecommenderFeedback => {
  if (!activityId || materialIds.length === 0) return feedback;

  const tabState: TAiLinkTabState = feedback.hasOpenedAiTab
    ? AI_LINK_TAB_STATE.OPENED_BEFORE_LINKING
    : AI_LINK_TAB_STATE.NO_TAB_OPEN;

  const remaining = feedback.items.filter(
    (item) =>
      !(item.activityId === activityId && materialIds.includes(item.materialId))
  );

  const added: TAiLinkFeedbackItem[] = materialIds.map((materialId) => ({
    activityId,
    materialId,
    tabState
  }));

  return {
    ...feedback,
    items: [...remaining, ...added]
  };
};

export const recordAiActivityUnlinked = (
  feedback: TAiRecommenderFeedback,
  {
    activityId,
    materialIds
  }: {
    activityId: string;
    materialIds?: string[];
  }
): TAiRecommenderFeedback => {
  if (!activityId) return feedback;

  return {
    ...feedback,
    items: feedback.items.filter((item) => {
      if (item.activityId !== activityId) return true;
      if (!materialIds?.length) return false;
      return !materialIds.includes(item.materialId);
    })
  };
};

export const markAiRecommendationTabOpened = (
  feedback: TAiRecommenderFeedback
): TAiRecommenderFeedback => ({
  hasOpenedAiTab: true,
  items: feedback.items.map((item) =>
    item.tabState === AI_LINK_TAB_STATE.NO_TAB_OPEN
      ? { ...item, tabState: AI_LINK_TAB_STATE.OPENED_AFTER_LINKING }
      : item
  )
});

export const withAiRecommenderFeedback = (
  entity: TEditingEntity | null,
  updater: (feedback: TAiRecommenderFeedback) => TAiRecommenderFeedback
): TEditingEntity | null => {
  if (!entity) return entity;
  return {
    ...entity,
    aiRecommenderFeedback: updater(getAiRecommenderFeedback(entity))
  };
};

export const toLinkRecommenderFeedbackPayload = (
  items: TAiLinkFeedbackItem[]
): TLinkRecommenderFeedbackRequestItem[] =>
  items.map((item) => ({
    activity_id: item.activityId,
    material_id: item.materialId,
    tab_state: item.tabState
  }));

export const buildAbandonedAiFeedbackItems = (
  editingEntity: TEditingEntity,
  selectedEntityInfo?: TEntityLink
): TAiLinkFeedbackItem[] => {
  const materialIds = getMaterialIdsForAiFeedback(
    editingEntity,
    selectedEntityInfo
  );
  if (!materialIds.length) return [];

  const tabState = getAiRecommenderFeedback(editingEntity).hasOpenedAiTab
    ? AI_LINK_TAB_STATE.OPENED_BEFORE_LINKING
    : AI_LINK_TAB_STATE.NO_TAB_OPEN;

  return materialIds.map((materialId) => ({
    activityId: null,
    materialId,
    tabState
  }));
};

export const resolveAiFeedbackPayload = ({
  editingEntity,
  selectedEntityInfo,
  forceAbandoned = false
}: {
  editingEntity: TEditingEntity;
  selectedEntityInfo?: TEntityLink;
  forceAbandoned?: boolean;
}): TLinkRecommenderFeedbackRequestItem[] => {
  const recorded = getAiRecommenderFeedback(editingEntity).items;
  if (!forceAbandoned && recorded.length) {
    return toLinkRecommenderFeedbackPayload(recorded);
  }
  return toLinkRecommenderFeedbackPayload(
    buildAbandonedAiFeedbackItems(editingEntity, selectedEntityInfo)
  );
};
